/**
 * Reading `GET /api/sites/:site/alarms/history`.
 *
 * The route is T14's and matches BMS-spec 0.74.9 as written; this module is the
 * vocabulary two consoles need to render its answer, kept here rather than in
 * either of them because both read the same rows for different audiences —
 * Admin's Events › Alarms tab for the technician, the User UI's Reports page
 * for the front office manager (GRMS §11.6, §14.2).
 *
 * Two facts about the route are encoded here rather than left to each caller:
 *
 *   - **`[from, to)` is half-open on `created_at`.** A range picked as "the last
 *     7 days" is seven whole days ending now, and the row created exactly at
 *     `to` is *outside* it. `alarmHistoryRange` computes both bounds so the two
 *     pages cannot disagree about what "7 days" means.
 *   - **`durationSeconds` is `resolvedAt − createdAt`, computed per request and
 *     never stored**, and is `null` while the alarm is open. It is therefore
 *     rendered as a dash rather than as a zero: an unresolved alarm has no
 *     duration yet, which is not the same as having taken no time.
 *
 * Pure, so it lives on the `@bms/common/utils` subpath: the package root opens
 * an MQTT client and an i18n instance on import, and a suite that mocks the
 * root has to spread the real utils back in.
 */
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/** `ALARM_TYPES` in `src/services/alarmService.ts`, in the order the spec lists them. */
export const ALARM_TYPE_OPTIONS = [
    { label: 'SOS', value: 'sos' },
    { label: 'Lock forced', value: 'lock_forced' },
    { label: 'Tamper', value: 'tamper' },
    { label: 'Burglary', value: 'burglary' },
    { label: 'HVAC deviation', value: 'hvac_deviation' },
    { label: 'Probe error', value: 'probe_error' },
    { label: 'Motion in vacant', value: 'motion_in_vacant' },
    { label: 'Overdue checkout', value: 'overdue_checkout' },
];
export const ALARM_STATUS_OPTIONS = [
    { label: 'Active', value: 'active' },
    { label: 'Acknowledged', value: 'acknowledged' },
    { label: 'Resolved', value: 'resolved' },
];
export function alarmTypeLabel(type) {
    return ALARM_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? String(type);
}
export function alarmStatusLabel(status) {
    return ALARM_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? String(status);
}
/**
 * `access.alarmType.*` / `access.alarmStatus.*` key for each option above, for
 * a caller that translates the alarm-history row it renders — this module
 * stays pure (no i18n import) by design, so it hands out the key rather than
 * calling `t()` itself; the caller does `t(ALARM_TYPE_LABEL_KEYS[type])`.
 */
export const ALARM_TYPE_LABEL_KEYS = {
    sos: 'access.alarmType.sos',
    lock_forced: 'access.alarmType.lockForced',
    tamper: 'access.alarmType.tamper',
    burglary: 'access.alarmType.burglary',
    hvac_deviation: 'access.alarmType.hvacDeviation',
    probe_error: 'access.alarmType.probeError',
    motion_in_vacant: 'access.alarmType.motionInVacant',
    overdue_checkout: 'access.alarmType.overdueCheckout',
};
/** See `ALARM_TYPE_LABEL_KEYS`. */
export const ALARM_STATUS_LABEL_KEYS = {
    active: 'access.alarmStatus.active',
    acknowledged: 'access.alarmStatus.acknowledged',
    resolved: 'access.alarmStatus.resolved',
};
/**
 * Severity ordered worst-first, for a list that wants to lead with the worst.
 * It is *not* used to sort the history: the route answers newest first, and a
 * reordered page would disagree with the `total` under it.
 */
export const ALARM_SEVERITY_RANK = {
    critical: 0,
    warning: 1,
    info: 2,
};
/** One tag, toned by severity: the type is the word, the colour says how bad. */
export const ALARM_SEVERITY_TONE = {
    critical: 'destructive',
    warning: 'warning',
    info: 'neutral',
};
export const ALARM_STATUS_TONE = {
    active: 'destructive',
    acknowledged: 'warning',
    resolved: 'neutral',
};
// ---------------------------------------------------------------------------
// The range
// ---------------------------------------------------------------------------
/** The ranges both consoles offer. Days, matching the KPI segment of §11.6. */
export const ALARM_RANGE_DAYS = [7, 30, 90];
/**
 * The `[from, to)` bounds of "the last `days` days", ending at `now`.
 *
 * `to` is the instant of the read rather than the end of today, because the
 * bound is exclusive and a `to` in the future would report a window that has
 * not happened yet as though it had.
 */
export function alarmHistoryRange(days, now = new Date()) {
    const to = new Date(now.getTime());
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
}
// ---------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------
/**
 * Whole seconds as a compact duration.
 *
 * `null` and `undefined` are "still open", not "took no time", and read as a
 * dash. A negative value cannot arise from the route — `resolved_at` is always
 * at or after `created_at` — but is floored at zero rather than rendered as a
 * negative duration if a clock ever disagrees.
 */
export function formatAlarmDuration(seconds) {
    if (seconds === null || seconds === undefined)
        return '—';
    if (seconds < 60)
        return `${Math.max(0, Math.round(seconds))} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const restMinutes = minutes % 60;
    if (hours < 24)
        return restMinutes ? `${hours} h ${restMinutes} min` : `${hours} h`;
    const days = Math.floor(hours / 24);
    const restHours = hours % 24;
    return restHours ? `${days} d ${restHours} h` : `${days} d`;
}
//# sourceMappingURL=alarmHistory.js.map